import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import Carbon16 from '@carbon/icons-react/es/carbon/16';
import EarthFilled16 from '@carbon/icons-react/es/earth--filled/16';
import Roadmap16 from '@carbon/icons-react/es/roadmap/16';
import Template16 from '@carbon/icons-react/es/template/16';
import moment, { ISO_8601 } from 'moment';
import React, {
  ChangeEvent,
  FC,
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import {
  CompactMenu,
  CompactMenuTitle,
} from 'components/atoms/menu/compact-menu';
import { RelativeTime } from 'components/atoms/relative-time';
import { Page } from 'components/molecules/page';
import { Caption01, ProductiveHeading05, Text02 } from 'components/styles';
import { RoutePaths } from 'constant';
import { setDirtyState } from 'data/templates';
import { TemplateState } from 'data/templates/api';
import {
  updateProjectTemplate,
  useActiveProjectTemplate,
  useActiveProjectTemplateStore,
  useHasSetupSpacesInTemplate,
} from 'data/templates/projects';
import { saveProjectTemplate } from 'data/templates/projects/actions';
import { selectDirtyState } from 'data/templates/selectors';
import { useTranslation } from 'hooks/use-translation';
import { getTaskTemplatesQueryKey, getTemplatesQueryKey } from 'queries';
import { fullName } from 'utils/full-name';
import { TemplateBreadcrump } from '../template-breadcrump';

const Header = styled(Page.Header)`
  && {
    ${Page.Header.Title} {
      width: 100vw;
    }

    ${Button} {
      margin: calc(-1 * var(--layout-02)) 0 0 var(--layout-03);
    }
  }
`;

const MultiLineInput = styled(Input)`
  ${ProductiveHeading05};
  background: transparent;
  outline: none;
  padding-left: 0;
  width: 50%;
  border-color: transparent;

  &:focus {
    border-bottom-color: var(--gray-50);
  }
`;

const InputWrapper = styled.div`
  flex: 1;

  .ant-form-item {
    padding: 0;
  }

  .ant-form-item-has-error ${MultiLineInput} {
    &,
    &:hover,
    &:focus {
      background: transparent;
      border-bottom-color: var(--error-color);
      outline: none;
    }
  }
`;

const LastSavedText = styled.span`
  ${Caption01};
  ${Text02};

  margin-top: calc(-1 * var(--layout-02));
`;

const FormItem = styled(Form.Item)`
  margin: 0;
`;

enum Tabs {
  Project = 'Project',
  Resource = 'Resource',
  Spaces = 'Spaces',
}

export const TemplateDetailHeader: FC = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const template = useActiveProjectTemplate();
  const { isSaving } = useActiveProjectTemplateStore();
  const spaceSetup = useHasSetupSpacesInTemplate();
  const history = useHistory();
  const { path } = useRouteMatch();
  const getSelectedTab = () => {
    if (path.indexOf('/resource-management') !== -1) {
      return Tabs.Resource;
    }
    if (path.indexOf('/spaces') !== -1) {
      return Tabs.Spaces;
    }

    return Tabs.Project;
  };

  const dirty = useSelector(selectDirtyState(template.templateId));

  const updateTemplateName = (value: string) => {
    dispatch(updateProjectTemplate({ ...template, templateName: value }));
  };

  const onSaveTemplate = (type: TemplateState) => {
    dispatch(
      saveProjectTemplate({
        type,
        handleSuccess() {
          dispatch(
            setDirtyState({
              id: template.templateId,
              _state: false,
            }),
          );
          queryClient.invalidateQueries(getTemplatesQueryKey());
        },
      }),
    );
  };

  const publish = () => {
    form.validateFields().then(() => {
      onSaveTemplate(TemplateState.Published);
      queryClient.invalidateQueries(getTaskTemplatesQueryKey());
    });
  };

  const [text, setText] = useState(template.templateName || '');
  const templatePlaceholderText = useTranslation(
    'templates.placeholders.template_name',
  );

  useEffect(() => {
    setText(template.templateName || '');

    form.setFieldsValue({ templateName: template.templateName });
  }, [form, template.templateName]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setText(e.currentTarget.value);
  };

  const handleOnUpdate = (e: KeyboardEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
    e.preventDefault();
  };

  const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    const latest = e.target.value.trim();
    const old = template.templateName?.trim();

    if (latest && latest !== old) {
      updateTemplateName(text);
    }
  };

  const next = () => {
    history.push(`/templates/projects/${template.templateId}/spaces`);
  };
  const onMenuSelected = useCallback(
    ({ key }) => {
      if (key === Tabs.Project) {
        history.push(`/templates/projects/${template.templateId}`);
      } else if (key === Tabs.Resource) {
        history.push(
          `/templates/projects/${template.templateId}/resource-management`,
        );
      } else {
        history.push(`/templates/projects/${template.templateId}/spaces`);
      }
    },
    [history, template.templateId],
  );

  return (
    <Header
      breadcrumbs={
        <TemplateBreadcrump
          route={RoutePaths.projectTemplateRoot}
          id="templates.projects"
        />
      }
      title={
        <Page.Header.Title>
          <InputWrapper>
            <Form form={form}>
              <FormItem
                name="templateName"
                rules={[
                  {
                    required: true,
                    message: 'Please enter a template name',
                  },
                ]}
              >
                <MultiLineInput
                  // autoFocus
                  value={text}
                  onPressEnter={handleOnUpdate}
                  onChange={handleOnChange}
                  onBlur={handleOnBlur}
                  placeholder={templatePlaceholderText}
                  data-cy="template-project-name"
                />
              </FormItem>
            </Form>
          </InputWrapper>
          {template.publishedAt && (
            <LastSavedText data-cy="templates.last_saved_at">
              <FormattedMessage
                id="templates.last_saved_at"
                values={{
                  by: template.publishedBy && fullName(template.publishedBy),
                  time: (
                    <RelativeTime
                      time={moment(template.publishedAt, ISO_8601)}
                    />
                  ),
                }}
              />
            </LastSavedText>
          )}
          {spaceSetup === 'COMPLETE' ? (
            <Button
              disabled={!dirty?.state}
              type="primary"
              iconPlacement="right"
              icon={<EarthFilled16 />}
              loading={isSaving}
              onClick={publish}
              data-cy="template-save"
            >
              <FormattedMessage id="save" />
            </Button>
          ) : (
            <Button
              type="primary"
              iconPlacement="right"
              icon={<ArrowRight16 />}
              onClick={next}
              data-cy="template-next"
            >
              <FormattedMessage id="next" />
            </Button>
          )}
        </Page.Header.Title>
      }
      tabs={
        <CompactMenu
          mode="horizontal"
          defaultSelectedKeys={[Tabs.Project]}
          selectedKeys={[getSelectedTab()]}
          onClick={onMenuSelected}
        >
          {/* TODO: add icons */}
          <CompactMenu.Item key={Tabs.Project} data-cy={Tabs.Project}>
            <CompactMenuTitle
              text={<FormattedMessage id="templates.projects.tabs.project" />}
              icon={<Template16 />}
            />
          </CompactMenu.Item>
          <CompactMenu.Item key={Tabs.Spaces} data-cy={Tabs.Spaces}>
            <CompactMenuTitle
              text={<FormattedMessage id="templates.projects.tabs.spaces" />}
              icon={<Carbon16 />}
            />
          </CompactMenu.Item>
          <CompactMenu.Item key={Tabs.Resource} data-cy={Tabs.Resource}>
            <CompactMenuTitle
              text={<FormattedMessage id="templates.projects.tabs.resource" />}
              icon={<Roadmap16 />}
            />
          </CompactMenu.Item>
        </CompactMenu>
      }
    />
  );
};
